import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import "./i18n.js";
import "./axiosConfig.js";
import { Workbox } from "workbox-window";
import { HelmetProvider } from 'react-helmet-async';

// Suppress ResizeObserver loop error (not critical, happens with MUI components)
const resizeObserverLoopErrRe = /^[^(ResizeObserver loop completed with undelivered notifications|ResizeObserver loop limit exceeded)]/;
const originalError = console.error;
console.error = (...args) => {
  if (typeof args[0] === 'string' && resizeObserverLoopErrRe.test(args[0])) {
    return;
  }
  originalError.apply(console, args);
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </React.StrictMode>
);
// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// Register Workbox service worker in production only
if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
  const wb = new Workbox(`${process.env.PUBLIC_URL}/service-worker.js`);

  // Prevent infinite refresh loops
  const MAX_REFRESH_COUNT = 3;
  const REFRESH_COUNT_KEY = 'sw_refresh_count';
  const REFRESH_TIMESTAMP_KEY = 'sw_refresh_timestamp';

  // When a new service worker is installed, force it to skipWaiting so it becomes active immediately
  wb.addEventListener('installed', (event) => {
    if (event.isUpdate) {
      try {
        // Ask the SW to skip waiting and activate immediately
        wb.messageSW({ type: 'SKIP_WAITING' });
      } catch (e) {
        console.warn('Failed to message SW to skip waiting', e);
      }
    } else {
      console.log('Content is cached for offline use.');
    }
  });

  // When this page's controlling service worker changes, clear all caches and reload the page
  wb.addEventListener('controlling', () => {
    const now = Date.now();
    const lastRefresh = parseInt(localStorage.getItem(REFRESH_TIMESTAMP_KEY) || '0');
    const refreshCount = parseInt(localStorage.getItem(REFRESH_COUNT_KEY) || '0');

    if (now - lastRefresh > 5 * 60 * 1000) {
      localStorage.setItem(REFRESH_COUNT_KEY, '0');
    }

    if (refreshCount >= MAX_REFRESH_COUNT) {
      console.warn('Refresh loop detected. Skipping auto-refresh. Please clear cache manually.');
      setTimeout(() => {
        localStorage.setItem(REFRESH_COUNT_KEY, '0');
      }, 10 * 60 * 1000);
      return;
    }

    // Clear all caches to avoid serving old assets
    if ('caches' in window) {
      caches.keys().then((cacheNames) => {
        return Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
      }).then(() => {
        localStorage.setItem(REFRESH_COUNT_KEY, (refreshCount + 1).toString());
        localStorage.setItem(REFRESH_TIMESTAMP_KEY, now.toString());
        window.location.reload();
      }).catch((err) => {
        console.error('Error clearing caches during SW activation:', err);
        // still attempt reload
        window.location.reload();
      });
    } else {
      localStorage.setItem(REFRESH_COUNT_KEY, (refreshCount + 1).toString());
      localStorage.setItem(REFRESH_TIMESTAMP_KEY, now.toString());
      window.location.reload();
    }
  });

  wb.register().catch((error) => {
    console.error("Error during service worker registration:", error);
    // If service worker fails to register, clear cache
    if ('caches' in window) {
      caches.keys().then((cacheNames) => {
        cacheNames.forEach((cacheName) => {
          caches.delete(cacheName);
        });
      });
    }
  });
}
else if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    registrations.forEach((registration) => registration.unregister());
  });
}
